import { Box, Button } from "@mui/material";

import dayjs from "dayjs";
import { useFormik } from "formik";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMemo, useState } from "react";
// import { useProvidersList } from "../../../../../queries/provider/setting/use-providers-list";
// import { Option } from "../../../../../constants/common";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
  // DEF_PAGINATION,
  // getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../../utils/utils";
import Text from "../../../../../components/Text";
import CustomSelect from "../../../../../components/CustomSelect";
import Flex from "../../../../../components/Flex";
import GlobalFilter from "../../../../../components/GlobalFilter";
import SelectDateRangePicker from "../../../../../components/SelectDateRangePicker";
// import { usePatientsList } from "../../../../../queries/use-get-patients";
// import { PAYMENT_OPTIONS } from "../../../../../components/app/provider/billing/payment/payment-constant";
import {
  CLAIM_TYPE_LIST,
  CLAIMS_STATUS_LIST,
  NEW_DATE_RANGE_FOR_CLAIMS_TYPE,
  // SCRUB_STATUS_LIST,
} from "./claims-constants";
import MaskMultiSelect from "../../../../../components/MaskMultiSelect";
import MaskMultiSelectScroll from "../../../../../components/MaskMultiSelectScroll";
import Input from "../../../../../components/Input";
import { useSearchPatientList } from "../../../../../queries/list/useSearchPatientList";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { useProviderListV2 } from "../../../../../queries/use-provider-list-v2";
import useActiveComponentCountController from "../../../../../components/filter/use-ActiveComponentCountController";
import { ClaimFilterData, DEFAULT_CLAIMS_FILTERS } from "../BillingsLayout";

interface ClaimsFiltersProps {
  onApply: (v: any) => void;
  filters: ClaimFilterData;
  setFilters: (v: ClaimFilterData) => void;
}

const ClaimsFilters = ({
  onApply,
  filters,
  setFilters,
}: ClaimsFiltersProps) => {
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: filters,
    onSubmit: (v) => {
      setFilters(v as ClaimFilterData);
      onApply(v as ClaimFilterData);
    },
  });
  const [providerSearchKey, setProviderSearchKey] = useDebounce<string>("");
  const [providerPagination, setProviderPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: providersList, isFetching: isFetchingProviders } =
    useProviderListV2({
      pagination: providerPagination,
      searchString: providerSearchKey,
      xTENANTID: getXtenantId(),
      status: true,
      archive: false,
    });

  const [patientSearchKey, setPatientSearchKey] = useDebounce<string>("");
  const [patientPagination, setPatientPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: patientList, isFetching: isFetchingPatients } =
    useSearchPatientList({
      searchString: patientSearchKey,
      pagination: patientPagination,
      xTENANTID: getXtenantId(),
    });

  const providerOptions = useMemo(() => {
    const fetched =
      providersList?.data?.content?.map((it) => ({
        value: it?.uuid,
        label: it?.name,
      })) ?? [];
    const selected = (formik.values.providers ?? []) as {
      [k: string]: string;
    }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) => !fetched.some((o) => o[idKey as keyof typeof o] === s[idKey])
    );
    return [...missing, ...fetched];
  }, [providersList, formik.values.providers]);

  const patientOptions = useMemo(() => {
    const fetched =
      patientList?.data?.content?.map((it) => ({
        value: it?.uuid,
        label: it?.firstName + " " + it?.lastName,
      })) ?? [];
    const selected = (formik.values.patients ?? []) as {
      [k: string]: string;
    }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) => !fetched.some((o) => o[idKey as keyof typeof o] === s[idKey])
    );
    return [...missing, ...fetched];
  }, [patientList, formik.values.patients]);

  const [expanded, setExpanded] = useState<{ [k: string]: boolean }[]>(
    Object.keys(formik.values).map((it) => ({ [it]: false }))
  );

  function handleExpandClick(k: string, value: boolean) {
    setExpanded((prev) =>
      prev?.map((it) => {
        const curKey = Object.keys(it)[0];

        return Object.keys(it).some((obj) => obj === k)
          ? { [k]: value }
          : { [curKey]: false };
      })
    );
  }

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const closeFilter = () => setAnchorEl(null);

  // startDate/endDate are part of the Date Range filter, not filters of their
  // own — counting them separately made a custom range read as 3 active
  // filters instead of 1.
  const countableFilters = Object.fromEntries(
    Object.entries(formik.values).filter(
      ([key]) => key !== "startDate" && key !== "endDate"
    )
  );
  const activeFilterCount = useActiveComponentCountController(countableFilters);

  return (
    <GlobalFilter
      anchorEl={anchorEl}
      setAnchorEl={setAnchorEl}
      activeFilterCount={activeFilterCount}
    >
      <Flex
        width={"18.438rem"}
        flexDirection={"column"}
        justifyContent={"space-between"}
      >
        <Box>
          <Flex width={"100%"} justifyContent={"end"}>
            <Button
              variant="text"
              onClick={() => {
                formik.resetForm();
                formik.submitForm();
                setFilters(DEFAULT_CLAIMS_FILTERS);
              }}
            >
              Clear Filter
            </Button>
          </Flex>
          {Object.keys(formik.values).map((key, ind) => {
            return key === "claim_statuses" ? (
              <Accordion
                variant="outlined"
                expanded={expanded[ind][key]}
                onChange={(_, newExpanded) =>
                  handleExpandClick(key, newExpanded)
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Text>Claim Status</Text>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 1 }}>
                  <MaskMultiSelect
                    optionWithCheckbox={true}
                    value={formik.values.claim_statuses}
                    placeholder="Claim Status"
                    options={CLAIMS_STATUS_LIST}
                    onChange={(val) => {
                      formik.setFieldValue("claim_statuses", val);
                    }}
                    // endIcon={<SearchIcon />}
                  />
                </AccordionDetails>
              </Accordion>
            ) : /* Scrub Status filter — commented out per ticket (kept for future use)
            key === "scrubStatus" ? (
              <Accordion
                variant="outlined"
                expanded={expanded[ind][key]}
                onChange={(_, newExpanded) =>
                  handleExpandClick(key, newExpanded)
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Text>Scrub Status</Text>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 1 }}>
                  <MaskMultiSelect
                    optionWithCheckbox={true}
                    value={formik.values.scrubStatus}
                    placeholder="Scrub Status"
                    options={SCRUB_STATUS_LIST}
                    onChange={(val) => {
                      formik.setFieldValue("scrubStatus", val);
                    }}
                  />
                </AccordionDetails>
              </Accordion>
            ) : */ key === "claimId" ? (
              <Accordion
                variant="outlined"
                expanded={expanded[ind][key]}
                onChange={(_, newExpanded) =>
                  handleExpandClick(key, newExpanded)
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Text>Claim ID</Text>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 1 }}>
                  <Input
                    textFieldProps={{
                      name: "claimId",
                      placeholder: "Claim ID",
                      value: formik.values.claimId ?? "",
                      onChange: (e) => {
                        formik.setFieldValue("claimId", e.target.value);
                      },
                    }}
                  />
                </AccordionDetails>
              </Accordion>
            ) : key === "claim_type" ? (
              <Accordion
                variant="outlined"
                expanded={expanded[ind][key]}
                onChange={(_, newExpanded) =>
                  handleExpandClick(key, newExpanded)
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Text>Claim Type</Text>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 1 }}>
                  <MaskMultiSelect
                    optionWithCheckbox={true}
                    value={formik.values.claim_type}
                    placeholder="Claim Type"
                    options={CLAIM_TYPE_LIST}
                    onChange={(val) => {
                      formik.setFieldValue("claim_type", val);
                    }}
                  />
                </AccordionDetails>
              </Accordion>
            ) : key === "date_range" ? (
              <Accordion
                variant="outlined"
                expanded={expanded[ind][key]}
                onChange={(_, newExpanded) =>
                  handleExpandClick(key, newExpanded)
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Text>{replaceUnderScoreAddSpace(key)}</Text>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 1 }}>
                  <CustomSelect
                    value={formik.values.date_range}
                    options={NEW_DATE_RANGE_FOR_CLAIMS_TYPE}
                    onChange={(v) => formik.setFieldValue("date_range", v)}
                  />
                  {formik.values.date_range === "CUSTOM_RANGE" && (
                    <Box sx={{ mt: 2 }}>
                      <SelectDateRangePicker
                        startDate={
                          formik.values.startDate
                            ? dayjs(formik.values.startDate)
                            : null
                        }
                        endDate={
                          formik.values.endDate
                            ? dayjs(formik.values.endDate)
                            : null
                        }
                        placeholder="Select Date Range"
                        onApply={(start, end) => {
                          formik.setFieldValue(
                            "startDate",
                            start ? start.toISOString() : undefined
                          );
                          formik.setFieldValue(
                            "endDate",
                            end ? end.toISOString() : undefined
                          );
                        }}
                        buttonSx={{ width: "100%" }}
                      />
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ) : key === "patients" ? (
              <Accordion
                variant="outlined"
                expanded={expanded[ind][key]}
                onChange={(_, newExpanded) =>
                  handleExpandClick(key, newExpanded)
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Text>{replaceUnderScoreAddSpace(key)}</Text>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 1 }}>
                  <MaskMultiSelectScroll
                    onSearch={(val) => setPatientSearchKey(val)}
                    optionWithCheckbox={true}
                    value={formik.values.patients}
                    placeholder="Patients"
                    options={patientOptions}
                    pagination={patientPagination}
                    setPagination={setPatientPagination}
                    totalElements={patientList?.data?.page?.totalElements}
                    loading={isFetchingPatients}
                    onChange={(val) => {
                      formik.setFieldValue("patients", val);
                    }}
                    // endIcon={<SearchIcon />}
                  />
                </AccordionDetails>
              </Accordion>
            ) : key === "providers" ? (
              <Accordion
                variant="outlined"
                expanded={expanded[ind][key]}
                onChange={(_, newExpanded) =>
                  handleExpandClick(key, newExpanded)
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Text>{replaceUnderScoreAddSpace(key)}</Text>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 1 }}>
                  <MaskMultiSelectScroll
                    onSearch={(val) => setProviderSearchKey(val)}
                    optionWithCheckbox={true}
                    value={formik.values.providers}
                    placeholder="providers"
                    options={providerOptions}
                    pagination={providerPagination}
                    setPagination={setProviderPagination}
                    totalElements={providersList?.data?.page?.totalElements}
                    loading={isFetchingProviders}
                    onChange={(val) => {
                      formik.setFieldValue("providers", val);
                    }}
                    // endIcon={<SearchIcon />}
                  />
                </AccordionDetails>
              </Accordion>
            ) : (
              <></>
            );
          })}
        </Box>
        <Flex width={"100%"} justifyContent={"end"}>
          <Button
            variant="text"
            sx={{ color: "gray" }}
            onClick={() => {
              formik.resetForm();
              formik.submitForm();
              closeFilter();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="text"
            onClick={() => {
              formik.submitForm();
              closeFilter();
            }}
          >
            Apply
          </Button>
        </Flex>
      </Flex>
    </GlobalFilter>
  );
};

export default ClaimsFilters;
